/**
 * Libraries
 */

import { createRouter, createWebHistory } from 'vue-router';
import { setupLayouts } from 'virtual:generated-layouts';
import { routes } from 'vue-router/auto-routes';

/**
 * Core
 */

import { trackPageView } from '@/core/configs/plausible';

/**
 * I18n
 */

import i18n from '@shared/i18n';

/**
 * Stores
 */

import { useKeycloakStore } from '@/stores/keycloak';
import { useToasterMessageStore } from '@/stores/toasterMessage';
import { useSettingsStore } from '@/stores/settings';
import { useTicketPermissionsStore } from '@/stores/ticketPermissions';

/**
 * Middleware
 */

import { runTicketNavigationGuard } from '@core/middleware/ticket';
import { resolveTicketNavigationRedirect } from '@core/middleware/resolveTicketNavigationRedirect';
import { routeTicketEnum } from '@core/router/routeNames';
import {
  handleGlobalAuth,
  handleAdminAuth,
  isUpsellRoutes,
  isUpsellAllowed,
} from '@core/middleware/auth';

/**
 * Types
 */

import { toasterTitle, typeOfMessage } from '@core/types/ToasterMessageTypes';
import { SettingsType } from '@generatedTypes';

import type { RouteLocationRaw } from 'vue-router';
import type { RoutesList } from '@core/router/routeNames';
import type {
  MetaTypes,
  MetaSubTypes,
  MetaLayouts,
  MetaErrors,
} from '@/core/router/routerMeta';

declare module 'vue-router' {
  interface RouteMeta {
    auth?: boolean;
    title?: string;
    type?: MetaTypes;
    subType?: MetaSubTypes;
    layout?: MetaLayouts;
    error?: MetaErrors;
    fullPage?: boolean;
    /** Locks the layout to the viewport height so children can scroll internally. */
    fixedHeight?: boolean;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes: setupLayouts(routes),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      /**
       * Returning the savedPosition will result in a native-like behavior when
       * navigating with back/forward buttons
       */
      return savedPosition;
    } else if (to.hash) {
      return {
        el: to.hash,
        // If supported by the browser
        behavior: 'smooth',
      };
    }
    return { top: 0 };
  },
});

router.beforeEach(async (to, from) => {
  const {
    getRoles,
    getUserRoles,
    isAdmin,
    isDqc,
    isOnlyAdmin,
    user,
    isTicketUser,
  } = useKeycloakStore();
  const { addNewMessage } = useToasterMessageStore();
  const settingsStore = useSettingsStore();

  const nextRoute = to.name as RoutesList;
  const code = (to.params as Record<string, string | string[]>).code;
  const userRoles = getUserRoles('lea');
  const hasRoles = getRoles().length > 0;

  const nextMetaType = to.meta.type;
  const errorMessageConfig = {
    title: toasterTitle.ACCESS_DENIED,
    content: i18n.global.t('app.errors.403'),
    type: typeOfMessage.DANGER,
  };

  const abortNavigation = (to: RouteLocationRaw) => {
    addNewMessage(errorMessageConfig);
    return router.push(to);
  };

  if (
    import.meta.env.VITE_APP_ALLOWED_DEV_ROUTES &&
    import.meta.env.VITE_APP_ALLOWED_DEV_ROUTES === 'allowed'
  ) {
    return;
  }

  // if the user has roles, we store his ticket permissions in the store if it's not already loaded
  if (hasRoles && userRoles) {
    const ticketPermissionsStore = useTicketPermissionsStore();
    if (!ticketPermissionsStore.loaded) {
      try {
        await ticketPermissionsStore.load();
      } catch {
        return {
          name: routeTicketEnum.TECHNICAL_ERROR,
          query: { retry: to.fullPath },
          replace: true,
        };
      }
    }

    // If the user is not an analytics user, he can't access the analytics pages, redirect him to the dashboard
    if (
      nextMetaType === 'analytics' &&
      (!isDqc || !settingsStore.getSetting(SettingsType.UpsellDqc).is_active)
    ) {
      return abortNavigation({ name: 'dashboard' });
    }

    // If the user is only an admin, he can only access the admin pages, redirect him to the admin_index
    if (nextMetaType !== 'admin' && nextMetaType !== 'profile' && isOnlyAdmin)
      return abortNavigation({ name: 'admin_index' });

    // If the user is not an admin, he can't access the admin pages, redirect him to the dashboard
    if (nextMetaType === 'admin' && !isAdmin)
      return abortNavigation({ name: 'dashboard' });

    // Handle routing for tickets routes, if the user is not allowed to access the route, redirect him to the dashboard
    if (nextMetaType === 'ticket' && code !== undefined) {
      const ticketNavigation = await runTicketNavigationGuard(
        to,
        from,
        isTicketUser,
      );

      if (ticketNavigation) {
        if (ticketNavigation.status === 'redirect') {
          addNewMessage(errorMessageConfig);
        }
        if (
          ticketNavigation.status === 'route_denied' ||
          ticketNavigation.status === 'forbidden'
        ) {
          return abortNavigation({ name: 'dashboard' });
        }

        return resolveTicketNavigationRedirect(ticketNavigation);
      }
    }

    // Handle routing for admin routes, if the user is not allowed to access the route, redirect him to the admin_index
    if (nextMetaType === 'admin' && isAdmin) {
      if (isUpsellRoutes(nextRoute) && !isUpsellAllowed(nextRoute))
        return abortNavigation({ name: 'admin_index' });
      if (!handleAdminAuth(userRoles, nextRoute, user))
        return abortNavigation({ name: 'admin_index' });
    }

    // Handle routing for the rest of the routes, if the user is not allowed to access the route, redirect him to the dashboard
    if (
      nextMetaType !== 'admin' &&
      nextMetaType !== 'ticket' &&
      !handleGlobalAuth(userRoles, nextRoute)
    )
      return abortNavigation({ name: 'dashboard' });
  }

  return;
});

router.afterEach((to, from) => {
  if (import.meta.env.VITE_PLAUSIBLE_ENABLED === 'true') {
    if (to.path === from.path && to.fullPath === from.fullPath) return;
    trackPageView(to.path, to.query);
  }
});

router.onError((error) => {
  console.error('router error', error);
});

export default router;
